/**
 * News article form with rich text editor
 * Handles create/edit workflow with draft/publish controls
 */
import { useState } from 'react'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { Textarea } from './ui/Textarea'
import { Alert } from './ui/Alert'
import { NewsEditor } from './NewsEditor'
import { sanitizeHTML } from '../utils/sanitize'
import type { NewsArticle, NewsArticleInput } from '../types'

interface NewsFormProps {
  article?: NewsArticle
  onSubmit: (data: NewsArticleInput) => Promise<void>
  onCancel: () => void
  isSubmitting?: boolean
}

export function NewsForm({ article, onSubmit, onCancel, isSubmitting }: NewsFormProps) {
  const [formData, setFormData] = useState<NewsArticleInput>({
    title: article?.title || '',
    slug: article?.slug || '',
    content: article?.content || '',
    excerpt: article?.excerpt || '',
    author: article?.author || '',
    published_at: article?.published_at,
  })
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }

    if ((formData.content?.length ?? 0) > 100000) {
      setError(
        `Content is too long (${(formData.content?.length ?? 0).toLocaleString()} characters). Maximum is 100,000.`
      )
      return
    }

    try {
      // Sanitize content before submission
      // Omit slug when blank — server auto-generates from title
      const sanitizedData = {
        ...formData,
        slug: formData.slug?.trim() || undefined,
        content: formData.content ? sanitizeHTML(formData.content) : undefined,
      }

      await onSubmit(sanitizedData)
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const handlePublishNow = () => {
    setFormData({ ...formData, published_at: Math.floor(Date.now() / 1000) })
  }

  const handleSaveDraft = () => {
    setFormData({ ...formData, published_at: null })
  }

  const isDraft = !formData.published_at
  const isScheduled = formData.published_at && formData.published_at > Math.floor(Date.now() / 1000)

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Big Win at Regionals!"
          maxLength={200}
          required
        />
      </div>

      {/* Slug */}
      <div>
        <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
          Slug (URL)
        </label>
        <Input
          id="slug"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          placeholder="big-win-at-regionals (auto-generated if empty)"
          maxLength={200}
        />
        <p className="text-xs text-gray-500 mt-1">
          Auto-generated from title if left empty. Used in URL: /news/your-slug
        </p>
      </div>

      {/* Excerpt */}
      <div>
        <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
          Excerpt
        </label>
        <Textarea
          id="excerpt"
          value={formData.excerpt}
          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
          placeholder="Brief summary shown in news list..."
          rows={2}
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-1">{formData.excerpt?.length || 0}/500 characters</p>
      </div>

      {/* Content (Rich Text) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
        <NewsEditor
          content={formData.content || ''}
          onChange={(html) => setFormData({ ...formData, content: html })}
        />
      </div>

      {/* Author */}
      <div>
        <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
          Author
        </label>
        <Input
          id="author"
          value={formData.author}
          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
          placeholder="Leave blank to use your account name"
          maxLength={100}
        />
      </div>

      {/* Publishing Status */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Publishing Status</label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={isDraft ? 'primary' : 'secondary'}
            onClick={handleSaveDraft}
          >
            {isDraft ? '📝 Draft' : 'Save as Draft'}
          </Button>
          <Button
            type="button"
            variant={!isDraft && !isScheduled ? 'primary' : 'secondary'}
            onClick={handlePublishNow}
          >
            {!isDraft && !isScheduled ? '✅ Published' : 'Publish Now'}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {isDraft && 'Draft articles are not visible to the public'}
          {isScheduled &&
            `Scheduled for ${new Date(formData.published_at! * 1000).toLocaleString()}`}
          {!isDraft && !isScheduled && 'Article is live on the public site'}
        </p>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : article ? 'Update Article' : 'Create Article'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
